import React from 'react'
import { BtcLightningIcon } from '@/components/icons/BtcLightningIcon'
import { CreditCardIcon } from '@/components/icons/CreditCardIcon'
import { CryptoPaymentMethodIcon } from '@/components/icons/CryptoPaymentMethodIcon'
import { WirePaymentMethodIcon } from '@/components/icons/WirePaymentMethodIcon'
import { PaymentMethodsType } from '@/types/paymentMethods.interface'
import {
  Box,
  ButtonGroup,
  Circle,
  Flex,
  IconButton,
  Text,
  Tooltip,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/router'
import { BitcoinIcon } from '@/components/icons/BitcoinIcon'
import { PAYMENT_TYPES, PAYMENT_METHODS } from '@/constants'
import { formatPrice } from '@/utils/formatPrice'
import { CheckIcon } from '@chakra-ui/icons'

interface IProps {
  isInvoice?: boolean
  isMaxCreditsApplied?: boolean
  enabledPaymentMethods?: Array<PaymentMethodsType>
  activePaymentButton?: PaymentMethodsType | null
  setActivePaymentButton: (button: PaymentMethodsType) => void
  creditCardLimit?: number
}

const PaymentMethods = ({
  isInvoice,
  isMaxCreditsApplied,
  enabledPaymentMethods,
  activePaymentButton = null,
  setActivePaymentButton,
  creditCardLimit,
}: IProps) => {
  const { pathname } = useRouter()
  const t = useTranslations()
  const activePaymentMethodBgColor = useColorModeValue('blue.50', 'gray.700')
  const activePaymentMethodColor = useColorModeValue('blackAlpha.900', 'white')
  const paymentMethodColor = useColorModeValue('blackAlpha.700', 'gray.100')
  const paymentMethodBgColor = useColorModeValue('gray.50', 'whiteAlpha.100')
  const paymentMethodBorderColor = useColorModeValue(
    'gray.200',
    'whiteAlpha.100'
  )
  const paymentButtons = {
    [PAYMENT_TYPES.CARD]: {
      icon: <CreditCardIcon boxSize={7} />,
      name: PAYMENT_METHODS.CARD,
    },
    [PAYMENT_TYPES.CRYPTO]: {
      icon: <CryptoPaymentMethodIcon boxSize={7} />,
      name: PAYMENT_METHODS.CRYPTO,
    },
    [PAYMENT_TYPES.LIGHTNING]: {
      icon: <BtcLightningIcon boxSize={7} />,
      name: PAYMENT_METHODS.LIGHTNING,
    },
    [PAYMENT_TYPES.BITCOIN]: {
      icon: <BitcoinIcon boxSize={7} />,
      name: PAYMENT_METHODS.BITCOIN,
    },
    [PAYMENT_TYPES.ACH]: {
      icon: <WirePaymentMethodIcon boxSize={7} />,
      name: PAYMENT_METHODS.ACH,
    },
    [PAYMENT_TYPES.CREDITS]: {
      icon: null,
      name: PAYMENT_METHODS.CREDITS,
      isHidden: true,
    },
  }

  const handleClickPaymentMethod = (method: PaymentMethodsType): void => {
    setActivePaymentButton(method)
  }

  const hasCardPayment = enabledPaymentMethods?.some((method) =>
    method.paymentProcessor.includes('card')
  )

  return (
    <Box w="full">
      <Text fontWeight="600" mb="12px">
        {t('checkout.pay-with')}
      </Text>
      <ButtonGroup w="full" justifyContent="center">
        <Flex flexWrap="nowrap" gap={2} py={2} justify="center" w="full">
          {enabledPaymentMethods?.map((method: PaymentMethodsType) => {
            // paymentProcessor from the backend needs to be available in frontend components too.
            // Some payment methods like credits may not be displayed
            if (
              !paymentButtons[method.paymentProcessor] ||
              !paymentButtons[method.paymentProcessor].icon ||
              paymentButtons[method.paymentProcessor].isHidden
            ) {
              return null
            }

            return (
              <React.Fragment key={method.paymentProcessor}>
                <Tooltip
                  label={
                    method.paymentProcessor === PAYMENT_TYPES.CARD &&
                    !pathname.includes('/billing/pay-invoices')
                      ? t('checkout.card-tooltip', {
                          creditCardLimit: formatPrice(
                            creditCardLimit || 25000,
                            false
                          ),
                        })
                      : ''
                  }
                  placement="top"
                  hasArrow
                  arrowSize={10}
                  rounded={'md'}
                >
                  <Box position="relative" flex="1 1 0" minW="0">
                    {activePaymentButton?.paymentProcessor ===
                      method.paymentProcessor && (
                      <Circle
                        size="18px"
                        bg="blue.500"
                        color="white"
                        position="absolute"
                        top="1"
                        right="1"
                        pointerEvents="none"
                        zIndex={1}
                      >
                        <CheckIcon boxSize={3} />
                      </Circle>
                    )}
                    <IconButton
                      borderWidth={1}
                      borderRadius="8px"
                      h="75px"
                      w="100%"
                      bg={
                        activePaymentButton?.paymentProcessor ===
                        method.paymentProcessor
                          ? activePaymentMethodBgColor
                          : paymentMethodBgColor
                      }
                      color={
                        activePaymentButton?.paymentProcessor ===
                        method.paymentProcessor
                          ? activePaymentMethodColor
                          : paymentMethodColor
                      }
                      _hover={{
                        bg: activePaymentMethodBgColor,
                        color: activePaymentMethodColor,
                      }}
                      borderColor={
                        activePaymentButton?.paymentProcessor ===
                        method.paymentProcessor
                          ? 'brand.600'
                          : paymentMethodBorderColor
                      }
                      data-selected={
                        activePaymentButton?.paymentProcessor ===
                        method.paymentProcessor
                      }
                      _focus={{
                        bg:
                          activePaymentButton?.paymentProcessor ===
                          method.paymentProcessor
                            ? activePaymentMethodBgColor
                            : paymentMethodBgColor,
                        color:
                          activePaymentButton?.paymentProcessor ===
                          method.paymentProcessor
                            ? activePaymentMethodColor
                            : 'black.500',
                      }}
                      icon={
                        <VStack spacing={1} lineHeight="1" align="center">
                          {paymentButtons[method.paymentProcessor].icon}
                          <Text fontSize="12px" textAlign={'center'}>
                            {isInvoice &&
                            method.paymentProcessor === PAYMENT_TYPES.CARD
                              ? t('buttons.payments.credit-debit-card')
                              : t(
                                  `buttons.payments.${paymentButtons[
                                    method.paymentProcessor
                                  ].name.toLowerCase()}`
                                )}
                          </Text>
                        </VStack>
                      }
                      aria-label={'-'}
                      onClick={() => handleClickPaymentMethod(method)}
                      isDisabled={isMaxCreditsApplied}
                      data-test-id={paymentButtons[
                        method.paymentProcessor
                      ].name.toLowerCase()}
                    />
                  </Box>
                </Tooltip>
              </React.Fragment>
            )
          })}
          {!hasCardPayment && !isInvoice && (
            <Tooltip
              label={t('checkout.card-tooltip', {
                creditCardLimit: formatPrice(creditCardLimit || 25000, false),
              })}
              placement="top"
              hasArrow
              arrowSize={10}
              rounded={'md'}
            >
              <Box flex="1 1 0" minW="0">
                <IconButton
                  borderWidth={1}
                  borderRadius="8px"
                  w="100%"
                  h="75px"
                  bg={paymentMethodBgColor}
                  color={paymentMethodColor}
                  borderColor={paymentMethodBorderColor}
                  icon={
                    <VStack spacing={1} lineHeight="1" align="center">
                      <CreditCardIcon boxSize={7} />
                      <Text fontSize="12px" textAlign={'center'}>
                        {t('buttons.payments.credit-debit-card')}
                      </Text>
                    </VStack>
                  }
                  aria-label={'-'}
                  isDisabled={true}
                />
              </Box>
            </Tooltip>
          )}
        </Flex>
      </ButtonGroup>
    </Box>
  )
}

export default PaymentMethods
